import type { Logger } from '../types/logger';
import type { LogAggregator, LogEntry, StructuredLogger } from '@mimir/primitive/structuredLogger';

/**
 * Apply a given a prefix to all messages.
 */
export class PrefixedLogger implements Logger {
  constructor(private inner: Logger, private prefix: string) { }

  log(entry: LogEntry): void {
    this.inner.log({
      ...entry,
      message: (entry.message !== undefined) ? `${this.prefix}${entry.message}` : undefined,
    });
  }

  child(info: Record<string, unknown>, aggregators?: Record<string, LogAggregator>): Logger {
    return new PrefixedLogger(this.inner.child(info, aggregators), this.prefix);
  }

  async flush(): Promise<void> {
    await this.inner.flush();
  }

  async close(): Promise<void> {
    await this.inner.close();
  }

  private wrap(msg: string): string {
    return `${this.prefix}${msg}`;
  }

  setSillyMode(enabled: boolean): void {
    this.inner.setSillyMode(enabled);
  }

  sillyModeEnabled(): boolean {
    return this.inner.sillyModeEnabled();
  }

  setDebugMode(enabled: boolean): void {
    this.inner.setDebugMode(enabled);
  }

  debugModeEnabled(): boolean {
    return this.inner.debugModeEnabled();
  }

  silly(message: string | (() => string)): void {
    this.inner.silly(() => {
      const msg = (typeof message === 'string') ? message : message();
      return this.wrap(msg);
    });
  }

  debug(message: string): void {
    this.inner.debug(this.wrap(message));
  }

  info(message: string): void {
    this.inner.info(this.wrap(message));
  }

  warn(message: string): void {
    this.inner.warn(this.wrap(message));
  }

  error(message: string): void {
    this.inner.error(this.wrap(message));
  }
}

export class PrefixedStructuredLogger implements StructuredLogger {
  constructor(private inner: StructuredLogger, private prefix: string) { }

  log(entry: LogEntry): void {
    this.inner.log({ ...entry, prefix: [...(entry.prefix ?? []), this.prefix]});
  }

  async close(): Promise<void> {
    return await this.inner.close();
  }
}
